import React from 'react';
import { Link } from 'react-router-dom';
import './CmsRenderer.css';

function embedVideoUrl(raw) {
  if (!raw) return '';
  const yt = raw.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = raw.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return raw;
}

function FaqBlock({ b }) {
  const [open, setOpen] = React.useState(null);
  return (
    <section className="cms-section">
      <div className="cms-container">
        {b.title && <h2 className="cms-section-title">{b.title}</h2>}
        <div className="cms-faq-list">
          {(b.items || []).map((it, i) => (
            <div
              key={i}
              className={`cms-faq-item${open === i ? ' cms-faq-item--open' : ''}`}
              onClick={() => setOpen(open === i ? null : i)}
            >
              <div className="cms-faq-q">
                <span>{it.q}</span>
                <span className="cms-faq-arrow">{open === i ? '−' : '+'}</span>
              </div>
              {open === i && <div className="cms-faq-a">{it.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function CmsRenderer({ blocks = [] }) {
  return (
    <>
      {blocks.map((b) => {

        if (b.type === 'hero') {
          const bgStyle = b.bgImage
            ? { backgroundImage: `url(${b.bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: b.bg || '#0f172a' };
          return (
            <section key={b.id} className="cms-hero" style={{ ...bgStyle, color: b.fg || '#fff', minHeight: b.minHeight || 400, textAlign: b.align || 'center' }}>
              {b.bgImage && <div className="cms-hero-overlay" />}
              <div className="cms-hero-inner">
                <h1 className="cms-hero-title">{b.title}</h1>
                {b.subtitle && <p className="cms-hero-sub">{b.subtitle}</p>}
                <div className="cms-btns">
                  {b.ctaText && <Link to={b.ctaLink || '/contact'} className="cms-btn cms-btn--primary">{b.ctaText}</Link>}
                  {b.ctaSecondaryText && <Link to={b.ctaSecondaryLink || '#'} className="cms-btn cms-btn--ghost">{b.ctaSecondaryText}</Link>}
                </div>
              </div>
            </section>
          );
        }

        if (b.type === 'features') return (
          <section key={b.id} className="cms-section">
            <div className="cms-container">
              {b.title && <h2 className="cms-section-title">{b.title}</h2>}
              {b.subtitle && <p className="cms-section-sub">{b.subtitle}</p>}
              <div className={`cms-grid cms-grid--${b.columns || 3}`}>
                {(b.items || []).map((it, i) => (
                  <div key={i} className="cms-feat-card">
                    <div className="cms-feat-icon">{it.icon}</div>
                    <div className="cms-feat-title">{it.title}</div>
                    <div className="cms-feat-text">{it.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

        if (b.type === 'stats') return (
          <section key={b.id} className="cms-section cms-stats-section">
            <div className="cms-container">
              {b.title && <h2 className="cms-section-title">{b.title}</h2>}
              <div className="cms-stats-row">
                {(b.items || []).map((it, i) => (
                  <div key={i} className="cms-stat">
                    <div className="cms-stat-value" style={{ color: it.color }}>{it.value}</div>
                    <div className="cms-stat-label">{it.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

        if (b.type === 'cta') return (
          <section key={b.id} className="cms-cta" style={{ background: b.bg || '#1e40af', color: b.fg || '#fff' }}>
            <div className="cms-container" style={{ textAlign: 'center' }}>
              <h2 className="cms-cta-title">{b.title}</h2>
              {b.subtitle && <p className="cms-cta-sub">{b.subtitle}</p>}
              <div className="cms-btns">
                {b.ctaText && <Link to={b.ctaLink || '/contact'} className="cms-btn cms-btn--white">{b.ctaText}</Link>}
                {b.ctaSecondaryText && <Link to={b.ctaSecondaryLink || '#'} className="cms-btn cms-btn--ghost-white">{b.ctaSecondaryText}</Link>}
              </div>
            </div>
          </section>
        );

        if (b.type === 'testimonials') return (
          <section key={b.id} className="cms-section">
            <div className="cms-container">
              {b.title && <h2 className="cms-section-title">{b.title}</h2>}
              <div className={`cms-grid cms-grid--${b.columns || 2}`}>
                {(b.items || []).map((it, i) => (
                  <div key={i} className="cms-testi">
                    <p className="cms-testi-quote">"{it.quote}"</p>
                    <div className="cms-testi-author">
                      {it.avatar && <img src={it.avatar} alt={it.author} className="cms-testi-avatar" />}
                      <div>
                        <div className="cms-testi-name">{it.author}</div>
                        <div className="cms-testi-role">{it.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

        if (b.type === 'faq') return <FaqBlock key={b.id} b={b} />;

        if (b.type === 'team') return (
          <section key={b.id} className="cms-section">
            <div className="cms-container">
              {b.title && <h2 className="cms-section-title">{b.title}</h2>}
              <div className={`cms-grid cms-grid--${b.columns || 3}`}>
                {(b.items || []).map((it, i) => (
                  <div key={i} className="cms-team-card">
                    {it.avatar
                      ? <img src={it.avatar} alt={it.name} className="cms-team-avatar" />
                      : <div className="cms-team-avatar cms-team-initials">{(it.name || '?')[0]}</div>}
                    <div className="cms-team-name">{it.name}</div>
                    <div className="cms-team-role">{it.role}</div>
                    {it.bio && <p className="cms-team-bio">{it.bio}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        );

        if (b.type === 'text') {
          const Tag = b.headingLevel || 'h2';
          return (
            <section key={b.id} className="cms-section" style={{ background: b.bg || undefined, color: b.fg || undefined, textAlign: b.align || 'left' }}>
              <div className="cms-container">
                {b.heading && <Tag className="cms-text-heading">{b.heading}</Tag>}
                {b.content && <p className="cms-text-content">{b.content}</p>}
              </div>
            </section>
          );
        }

        if (b.type === 'image') return (
          <section key={b.id} className="cms-section" style={{ textAlign: b.align || 'center' }}>
            <div className="cms-container">
              {b.url && (
                <img
                  src={b.url} alt={b.alt || ''}
                  className={`cms-img${b.rounded ? ' cms-img--rounded' : ''}${b.shadow ? ' cms-img--shadow' : ''}`}
                  style={{ maxWidth: b.maxWidth || 800 }}
                  loading="lazy"
                />
              )}
              {b.caption && <p className="cms-caption">{b.caption}</p>}
            </div>
          </section>
        );

        if (b.type === 'gallery') return (
          <section key={b.id} className="cms-section">
            <div className="cms-container">
              {b.title && <h2 className="cms-section-title">{b.title}</h2>}
              <div className={`cms-gallery cms-grid--${b.columns || 3}`} style={{ gap: b.gap || 12 }}>
                {(b.items || []).map((it, i) => (
                  <img key={i} src={it.url} alt={it.caption || ''} className="cms-gallery-img" loading="lazy" />
                ))}
              </div>
            </div>
          </section>
        );

        if (b.type === 'video') {
          const src = embedVideoUrl(b.url);
          return src ? (
            <section key={b.id} className="cms-section" style={{ textAlign: 'center' }}>
              <div className="cms-container">
                {b.title && <h2 className="cms-section-title">{b.title}</h2>}
                <div className="cms-video-wrap">
                  <iframe src={src} title={b.title || 'Vidéo'} allowFullScreen frameBorder="0" loading="lazy" />
                </div>
                {b.caption && <p className="cms-caption">{b.caption}</p>}
              </div>
            </section>
          ) : null;
        }

        if (b.type === 'divider') return (
          <div key={b.id} style={{ padding: `${b.margin || 40}px 40px` }}>
            <hr style={{ borderStyle: b.style || 'solid', borderColor: b.color || '#e2e8f0', borderWidth: '1px 0 0', margin: 0 }} />
          </div>
        );

        if (b.type === 'spacer') return (
          <div key={b.id} style={{ height: b.height || 60 }} />
        );

        return null;
      })}
    </>
  );
}
